// src/utils/contentLoader.ts
import matter from 'gray-matter';

const WORDS_PER_PAGE = 150; // Configurable words per page

interface PageBreak {
  pageNumber: number;
  content: string[];
}

// Cache for chapter pages
const chapterPagesCache: Record<number, PageBreak[]> = {};

export async function getChapterContent(chapterNumber: number, pageNumber: number) {
  try {
    const chapter = await getChapterMetadata(chapterNumber);
    if (!chapter) return null;

    const response = await fetch(`/content/chapters/${chapter.fileName}`);
    if (!response.ok) return null;

    const fileContent = await response.text();
    const { content } = matter(fileContent);

    if (!chapterPagesCache[chapterNumber]) {
      chapterPagesCache[chapterNumber] = splitContentIntoPages(content);
    }
    const pages = chapterPagesCache[chapterNumber];

    return pages.find(p => p.pageNumber === pageNumber)?.content || null;
  } catch (error) {
    console.error('Error loading chapter:', error);
    return null;
  }
}

function splitContentIntoPages(content: string): PageBreak[] {
  const paragraphs = content.split('\n\n').filter(p => p.trim());
  const pages: PageBreak[] = [];
  let currentPage: string[] = [];
  let currentWordCount = 0;
  let pageNumber = 1;

  for (const paragraph of paragraphs) {
    const paragraphWordCount = paragraph.split(/\s+/).length;

    // Check if adding this paragraph would exceed the page limit
    if (currentWordCount + paragraphWordCount > WORDS_PER_PAGE && currentPage.length > 0) {
      // Store current page and start a new one
      pages.push({
        pageNumber,
        content: currentPage
      });
      currentPage = [paragraph];
      currentWordCount = paragraphWordCount;
      pageNumber++;
    } else {
      // Add paragraph to current page
      currentPage.push(paragraph);
      currentWordCount += paragraphWordCount;
    }
  }

  // Add the last page if it has content
  if (currentPage.length > 0) {
    pages.push({
      pageNumber,
      content: currentPage
    });
  }

  return pages;
}

export async function getChapterMetadata(chapterNumber: number) {
  const chaptersMetadata = await import('../content/metadata/chapters.json');
  return chaptersMetadata.chapters.find(c => c.id === chapterNumber) || null;
}

export function getTotalPages(chapterNumber: number): number {
  return chapterPagesCache[chapterNumber]?.length || 0;
}
